import Dexie, { Table } from 'dexie';
import { trace } from '@opentelemetry/api';
import { db } from '@database/db';
import { contexteUtilisateur, enregistrerErreurTelemetrie } from '@services/core/telemetrie';

/**
 * STOCKAGE UNITAIRE « OMBRE » DES DOSSIERS — phase 1 de la sortie du dossier monolithe.
 *
 * Aujourd'hui la copie locale d'un dossier est UN seul enregistrement IndexedDB (`bi`),
 * réécrit en entier à chaque action : c'est ce qui fige les tablettes sur les gros
 * dossiers (voir la mémoire projet atlas-v2-stockage-local). La cible : une ligne par
 * phrase, question, photo… écrite seule.
 *
 * PHASE 1 (ce fichier) — AUCUN changement de comportement :
 *  - le monolithe reste lu et écrit exactement comme avant ;
 *  - ce stockage unitaire est tenu À CÔTÉ, dans une base séparée (`atlas-ombre`, aucune
 *    migration du schéma de la base principale) ;
 *  - il est alimenté par le MÊME flux que ce qui part au serveur (ajouts, patchs,
 *    suppressions de DynamicService), et semé depuis les dossiers reçus du serveur ;
 *  - à l'ouverture d'un dossier, il est comparé au monolithe et les écarts remontent en
 *    télémétrie (`dossier.ombre.comparaison`). Un écart = soit un oubli du stockage
 *    unitaire (à corriger avant la bascule), soit une modification restée sur la tablette
 *    sans jamais partir au serveur (risque de perte existant, à corriger aussi).
 *
 * Tout est protégé : une erreur ici ne doit JAMAIS bloquer ni faire échouer l'appli.
 * Coupure d'urgence sur un poste : localStorage `atlas.ombre` = `off`.
 */

/** Entités suivies : le contenu d'un rapport saisi sur la tablette. */
const ENTITES = ['PhraseRapport', 'QuestionRapport', 'Photo', 'RapportInvestigation', 'RapportComment', 'Rapport'] as const;
type Entite = typeof ENTITES[number];

/**
 * Champs ignorés dans la comparaison : état local ou d'exécution, horodatages (serveur
 * et tablette ne les écrivent pas au même moment), définitions et copies de référentiel
 * (ce ne sont pas des données saisies), champs gérés par le serveur.
 */
const CHAMPS_IGNORES = new Set([
	'objectUrl', 'urlComplete', '_indisponible', '_runtimePrepared', 'isSync', 'uploadBlocked',
	'lastError', 'lastAttempt', 'update', 'file', 'manualPhotos', 'isSynced',
	'createdAt', 'updatedAt', 'localPath', 's3Path', 's3Synced', 'thumbS3',
	'phraseSnapshot', 'questionSnapshot', 'phraseVersion', 'hasPdf', 'pdfS3Synced', 'pdfPath',
	// Indicateur local « à régénérer » (conclusions) : jamais envoyé, propre à l'appareil.
	'dirty',
	// Version du tableau de dégâts (ATLAS-230) : jeton et champs de contrôle d'envoi.
	'tableauDegatVersion', 'tableauDegatVersionBase', 'tableauDegatForce',
]);

/** Enfants d'un rapport : suivis comme entités à part, pas comme champs du rapport. */
const COLLECTIONS_RAPPORT = new Set(['phrases', 'photos', 'investigations', 'commentaires', 'dommages', 'questions']);

interface LigneOmbre {
	entite: Entite;
	id: string;
	biId: number;
	donnees: Record<string, unknown>;
	/**
	 * PHASE 2, étape A : la ligne COMPLÈTE, de quoi reconstruire le dossier (relations
	 * comprises, sans champs d'exécution ni sous-collections suivies à part).
	 */
	complete?: Record<string, unknown>;
	/** `complete` est fiable : posée en entier (semis, ajout) — pas seulement par patchs. */
	completeOk?: boolean;
	/** Phrase parente d'une QuestionRapport (sa place dans le dossier). */
	parent?: string;
	/** Rang dans la liste d'origine (ordre des phrases, photos…), quand il est connu. */
	ordre?: number;
	/**
	 * Dernière opération locale appliquée (ms). Absent : ligne telle que semée. Sert à dire,
	 * en cas d'écart, quel côté est en retard : une ligne modifiée par une opération que le
	 * monolithe n'a pas = écriture du monolithe perdue ; sans opération = dérive côté monolithe.
	 */
	majLe?: number;
}

interface DossierOmbre {
	biId: number;
	rapportId: number | null;
	/** serveur : semé depuis le dossier reçu du serveur ; local : depuis la copie locale. */
	source: 'serveur' | 'local';
	semeLe: number;
	/** Patchs reçus pour une ligne absente (signal d'un oubli). */
	patchsSansLigne: number;
	/** Dernière activité (semis, comparaison) : sert au ménage. */
	vuLe?: number;
	/** PHASE 2, étape A : le dossier lui-même (client, adresses, dates…), sans le rapport. */
	bi?: Record<string, unknown>;
	/**
	 * Suivi complet demandé : dossier validé mais OUVERT sur cet appareil (travail du BE).
	 * Ses données complètes sont alors gardées, y compris aux resemis serveur.
	 */
	complet?: boolean;
}

/** Un dossier sans activité depuis ce délai est retiré de l'ombre (stockage limité des iPad). */
const DUREE_CONSERVATION_MS = 30 * 24 * 3600 * 1000;

class BaseOmbre extends Dexie {
	dossiers!: Table<DossierOmbre, number>;
	lignes!: Table<LigneOmbre, [string, string]>;
	constructor() {
		super('atlas-ombre');
		this.version(1).stores({
			dossiers: 'biId, rapportId',
			lignes: '[entite+id], biId',
		});
	}
}

// ─── Projection : les données saisies, normalisées pour la comparaison ─────────────

const LONGUEUR_MAX_TEXTE = 4000;

function hacher(texte: string): string {
	let h = 0;
	for (let i = 0; i < texte.length; i++) h = (h * 31 + texte.charCodeAt(i)) | 0;
	return `${texte.length}:${(h >>> 0).toString(36)}`;
}

/**
 * Retire récursivement les valeurs « vides » ({} [] null '') : `{"values": {},
 * "colorAssignments": {}}` et l'absence de workflowData disent la même chose (rien de
 * saisi) — sans cela, chaque phrase non remplie ressortait en écart (28/09).
 */
function sansVide(valeur: any, vus = new WeakSet<object>()): any {
	if (valeur === null || valeur === undefined || valeur === '') return undefined;
	if (typeof valeur !== 'object') return valeur;
	if (vus.has(valeur)) return undefined;
	vus.add(valeur);
	if (Array.isArray(valeur)) {
		const liste = valeur.map(v => sansVide(v, vus)).filter(v => v !== undefined);
		return liste.length ? liste : undefined;
	}
	const sortie: any = {};
	for (const [k, v] of Object.entries(valeur)) {
		const nettoye = sansVide(v, vus);
		if (nettoye !== undefined) sortie[k] = nettoye;
	}
	return Object.keys(sortie).length ? sortie : undefined;
}

function jsonStable(valeur: unknown): string {
	const vus = new WeakSet<object>();
	const trier = (v: any): any => {
		if (!v || typeof v !== 'object') return v;
		if (vus.has(v)) return undefined;
		vus.add(v);
		if (Array.isArray(v)) return v.map(trier);
		return Object.keys(v).sort().reduce((acc: any, k) => { acc[k] = trier(v[k]); return acc; }, {});
	};
	return JSON.stringify(trier(valeur)) ?? 'null';
}

/**
 * Réduit une entité à ses données saisies : valeurs simples, relations ramenées à leur
 * identifiant (`phrase: {id}` → `phraseId`), structures simples en JSON stable. Les
 * listes d'entités (phrases, photos…) sont suivies à part et ignorées ici.
 */
export function projeter(entite: Record<string, any>): Record<string, unknown> {
	const sortie: Record<string, unknown> = {};
	if (!entite || typeof entite !== 'object') return sortie;
	for (const [cle, valeur] of Object.entries(entite)) {
		if (CHAMPS_IGNORES.has(cle) || COLLECTIONS_RAPPORT.has(cle) || cle.startsWith('_')) continue;
		// undefined n'est pas envoyé au serveur (JSON) : pour un patch, la valeur serveur
		// reste inchangée — l'ombre fait de même.
		if (valeur === undefined) continue;
		if (valeur === null || typeof valeur !== 'object') {
			sortie[cle] = typeof valeur === 'string' && valeur.length > LONGUEUR_MAX_TEXTE ? hacher(valeur) : valeur;
			continue;
		}
		if (valeur instanceof Date) {
			sortie[cle] = isNaN(valeur.getTime()) ? null : valeur.toISOString();
			continue;
		}
		if (!Array.isArray(valeur) && 'id' in valeur) {
			// Relation : seule l'identité compte (la définition vient du référentiel).
			if (!((cle + 'Id') in entite)) sortie[cle + 'Id'] = (valeur as any).id ?? null;
			continue;
		}
		if (Array.isArray(valeur) && valeur.some(e => e && typeof e === 'object' && 'id' in e)) continue;
		const nettoye = sansVide(valeur);
		// Structure vide : « vide » (null ≡ absent à la comparaison). Pas « ignorée » : dans un
		// patch, elle REMPLACE la valeur serveur, l'ombre doit faire de même.
		if (nettoye === undefined) { sortie[cle] = null; continue; }
		const json = jsonStable(nettoye);
		sortie[cle] = json.length > LONGUEUR_MAX_TEXTE ? hacher(json) : json;
	}
	return sortie;
}

/** Égalité tolérante : null ≡ absent ≡ '', 1 ≡ true, '12' ≡ 12, dates équivalentes. */
function egales(a: unknown, b: unknown): boolean {
	const norm = (v: unknown): unknown => {
		if (v === undefined || v === '' ) return null;
		if (v === true) return 1;
		if (v === false) return 0;
		if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
		// Dates : « 2026-09-28T08:12:00.000Z » et « 2026-09-28 08:12:00 » (formats serveur /
		// tablette) doivent se comparer à la seconde près.
		if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(v)) {
			const t = Date.parse(v.includes('T') ? v : v.replace(' ', 'T') + 'Z');
			if (!isNaN(t)) return Math.floor(t / 1000);
		}
		return v;
	};
	return norm(a) === norm(b);
}

/** Toutes les entités suivies d'un dossier (tel que stocké dans le monolithe ou reçu du serveur). */
function extraire(bi: any): { entite: Entite; id: string; donnees: Record<string, unknown> }[] {
	const r = bi?.rapport;
	if (!r) return [];
	const sortie: { entite: Entite; id: string; donnees: Record<string, unknown> }[] = [];
	const ajouter = (entite: Entite, e: any) => {
		if (e?.id == null) return;
		sortie.push({ entite, id: String(e.id), donnees: projeter(e) });
	};
	ajouter('Rapport', r);
	for (const p of r.phrases || []) {
		ajouter('PhraseRapport', p);
		for (const q of p?.questions || []) ajouter('QuestionRapport', q);
	}
	for (const p of r.photos || []) ajouter('Photo', p);
	for (const i of r.investigations || []) ajouter('RapportInvestigation', i);
	for (const c of r.commentaires || []) ajouter('RapportComment', c);
	return sortie;
}

// ─── Données complètes (phase 2, étape A) ───────────────────────────────────────────

/** Champs d'exécution : jamais stockés (URL d'images, états d'envoi, fichiers…). */
const CHAMPS_EXECUTION = new Set([
	'objectUrl', 'urlComplete', 'file', 'manualPhotos', 'isSync', 'isSynced', 'uploadBlocked',
	'lastError', 'lastAttempt', 'update',
]);
/** Sous-collections suivies comme lignes à part (ne pas les dupliquer dans la ligne). */
const SOUS_COLLECTIONS: Partial<Record<Entite | 'Bi', Set<string>>> = {
	Rapport: new Set(['phrases', 'photos', 'investigations', 'commentaires']),
	PhraseRapport: new Set(['questions']),
	Bi: new Set(['rapport']),
};
/** Références vers le parent (cycles) : remplacées par l'identifiant. */
const REFERENCES_PARENT = new Set(['rapport', 'phraseRapport', 'bi']);

/** Copie JSON sans cycles (objets déjà vus → ignorés). Les Date deviennent des chaînes ISO. */
function cloner(valeur: unknown): unknown {
	const vus = new WeakSet<object>();
	return JSON.parse(JSON.stringify(valeur, (_cle, v) => {
		if (v && typeof v === 'object') {
			if (vus.has(v)) return undefined;
			vus.add(v);
		}
		return v;
	}) ?? 'null');
}

/** Ligne complète, prête à être reconstruite (relations conservées, parents → id). */
export function versComplet(entite: Entite | 'Bi', e: Record<string, any>): Record<string, unknown> {
	const sortie: Record<string, unknown> = {};
	if (!e || typeof e !== 'object') return sortie;
	const exclues = SOUS_COLLECTIONS[entite];
	for (const [cle, valeur] of Object.entries(e)) {
		if (valeur === undefined || CHAMPS_EXECUTION.has(cle) || cle.startsWith('_') || exclues?.has(cle)) continue;
		if (REFERENCES_PARENT.has(cle) && valeur && typeof valeur === 'object' && !Array.isArray(valeur)) {
			if (!((cle + 'Id') in e)) sortie[cle + 'Id'] = (valeur as any).id ?? null;
			continue;
		}
		sortie[cle] = valeur && typeof valeur === 'object' ? cloner(valeur) : valeur;
	}
	return sortie;
}

/** Valeur normalisée pour comparer deux lignes complètes (dates à la seconde, 1≡true…). */
function normaliser(v: any): any {
	if (v === undefined || v === '' || v === null) return null;
	if (v instanceof Date) return isNaN(v.getTime()) ? null : Math.floor(v.getTime() / 1000);
	if (v === true) return 1;
	if (v === false) return 0;
	if (typeof v === 'string') {
		const t = v.trim();
		if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) return Number(t);
		// Rendus HTML : l'adresse des photos (blob, miniature, pixel d'attente, REPLACE_id)
		// dépend de l'affichage et du moment, pas de la donnée — la photo est désignée par
		// data-imageid. L'appareil garde le rendu « affiché », le serveur et l'ombre le rendu
		// « nettoyé » : sans ça, chaque phrase avec photo ressortait en écart (07/10).
		if (t.startsWith('<') || t.startsWith('-')) {
			// Préfixe « - » ajouté à l'affichage aux conclusions générées (phrases verrouillées
			// comprises, jamais envoyé pour elles) : sans incidence sur la donnée.
			let html = v.replace(/^(\s*(?:<[^>]+>\s*)*)-\s+/, '$1');
			if (html.includes('<img')) html = html.replace(/(<img\b[^>]*?\s)src="[^"]*"/g, '$1src=""');
			return html;
		}
		if (t.includes('<img')) return v.replace(/(<img\b[^>]*?\s)src="[^"]*"/g, '$1src=""');
		if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(t)) {
			const ms = Date.parse(t.includes('T') ? t : t.replace(' ', 'T') + 'Z');
			if (!isNaN(ms)) return Math.floor(ms / 1000);
		}
		return v;
	}
	if (Array.isArray(v)) {
		const liste = v.map(normaliser).filter(x => x !== null);
		return liste.length ? liste : null;
	}
	if (typeof v === 'object') {
		const sortie: any = {};
		for (const k of Object.keys(v).sort()) {
			const n = normaliser(v[k]);
			if (n !== null) sortie[k] = n;
		}
		return Object.keys(sortie).length ? sortie : null;
	}
	return v;
}

/** Champs non bloquants dans la reconstruction (horodatages écrits à des moments différents). */
// phraseWorkflow : objet complet hydraté sur l'appareil (catalogue, serveur) ; l'ombre ne
// garde que phraseWorkflowId, comparé lui. À la reconstruction (étape D), l'objet se
// réhydrate depuis le catalogue local des workflows.
// Champs gérés par le serveur après l'envoi d'une photo (copie S3, miniature) : renseignés
// à des moments différents dans la copie du dossier et dans l'ombre — faux écarts (35767, 12/10).
const CHAMPS_INFORMATIFS = new Set(['createdAt', 'updatedAt', 'phraseSnapshot', 'questionSnapshot', 'phraseWorkflow',
	's3Synced', 'thumbS3', 's3Path', 'localPath', 'pdfS3Synced', 'pdfPath',
	// Contrôle de version du tableau de dégâts (ATLAS-230) : champs d'envoi, propres à
	// l'appareil — faux écarts sur 3 dossiers le jour de la livraison 2.1.46.
	'tableauDegatVersion', 'tableauDegatVersionBase', 'tableauDegatForce']);

/** Entités d'un dossier avec leur place (parent, rang) et leur ligne complète. */
const BALISES_ESPACES = ' [balises/espaces]';

/**
 * Pour les textes : un court extrait autour de la première différence (appareil / ombre),
 * balises retirées, pour comprendre un écart de rendu sans deviner.
 */
function extraitDifference(a: unknown, o: unknown): string {
	// Champs non textuels (phraseId, isConclusion…) : les deux valeurs, pour savoir
	// lequel des deux côtés est juste (36951, 20/10).
	if (typeof a !== 'string' || typeof o !== 'string') return ` [M:${valeurCourte(a)} O:${valeurCourte(o)}]`;
	const nettoyer = (t: string) => t.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ');
	const x = nettoyer(a), y = nettoyer(o);
	let i = 0;
	while (i < x.length && i < y.length && x[i] === y[i]) i++;
	if (i === x.length && i === y.length) return BALISES_ESPACES;
	const debut = Math.max(0, i - 12);
	return ` [M:«${x.slice(debut, i + 18)}» O:«${y.slice(debut, i + 18)}»]`;
}

function valeurCourte(v: unknown): string {
	if (v === undefined) return '∅';
	let t: string;
	try { t = JSON.stringify(v) ?? String(v); } catch { t = String(v); }
	return t.length > 40 ? `${t.slice(0, 40)}…` : t;
}

/** Le monolithe n'a aucune phrase / aucune photo alors que l'ombre en a. */
function monolitheVide(monolithe: any, ombre: { entite: string }[]): { phrases: number; photos: number } {
	const r = monolithe?.rapport ?? {};
	const dansOmbre = (e: string) => ombre.some(l => l.entite === e);
	return {
		phrases: !(r.phrases?.length) && dansOmbre('PhraseRapport') ? 1 : 0,
		photos: !(r.photos?.length) && dansOmbre('Photo') ? 1 : 0,
	};
}

/** Section d'une phrase (conclusion, remarques, investigation…) : son rang n'y vaut que là. */
function sectionPhrase(p: any): string {
	return `${p?.investigationId ?? p?.investigation?.id ?? '-'}|${p?.isConclusion ? 1 : 0}|${p?.isRemarque ? 1 : 0}`;
}

/** Rang d'affichage d'une phrase ; sans rang, en fin de liste. */
function rangPhrase(p: any): number {
	const r = Number(p?.sort);
	return Number.isFinite(r) ? r : Number.MAX_SAFE_INTEGER;
}

function extraireComplet(bi: any): { entite: Entite; id: string; complete: Record<string, unknown>; parent?: string; ordre?: number }[] {
	const r = bi?.rapport;
	if (!r) return [];
	const sortie: { entite: Entite; id: string; complete: Record<string, unknown>; parent?: string; ordre?: number }[] = [];
	const ajouter = (entite: Entite, e: any, ordre?: number, parent?: string) => {
		if (e?.id == null) return;
		sortie.push({ entite, id: String(e.id), complete: versComplet(entite, e), parent, ordre });
	};
	ajouter('Rapport', r);
	(r.phrases || []).forEach((p: any, i: number) => {
		ajouter('PhraseRapport', p, i);
		(p?.questions || []).forEach((q: any, j: number) => ajouter('QuestionRapport', q, j, p?.id != null ? String(p.id) : undefined));
	});
	(r.photos || []).forEach((p: any, i: number) => ajouter('Photo', p, i));
	(r.investigations || []).forEach((x: any, i: number) => ajouter('RapportInvestigation', x, i));
	(r.commentaires || []).forEach((c: any, i: number) => ajouter('RapportComment', c, i));
	return sortie;
}

/** Une ligne créée sur l'appareil depuis moins que ça est gardée au resemis (envoi en cours). */

// ─── Service ───────────────────────────────────────────────────────────────────────

export type ActionOmbre = 'add' | 'bulkAdd' | 'update' | 'patch' | 'bulkPatch' | 'delete' | 'bulkDelete';

class OmbreDossier {
	private base: BaseOmbre | null = null;
	/** Opérations appliquées une par une, dans l'ordre d'arrivée. */
	private file: Promise<void> = Promise.resolve();

	/** Vrai si l'entité fait partie du contenu suivi (évite tout travail sinon). */
	suit(entite: string): boolean {
		return ((ENTITES as readonly string[]).includes(entite) || entite === 'Bi') && this.actif();
	}

	private actif(): boolean {
		try {
			if (typeof indexedDB === 'undefined') return false;
			return localStorage.getItem('atlas.ombre') !== 'off';
		} catch {
			return true;
		}
	}

	private ouvrir(): BaseOmbre {
		if (!this.base) this.base = new BaseOmbre();
		return this.base;
	}

	private enFile(nom: string, travail: () => Promise<void>): void {
		if (!this.actif()) return;
		this.file = this.file.then(travail).catch(erreur => {
			console.warn(`⚠️ [ombre] ${nom} :`, erreur);
			enregistrerErreurTelemetrie(erreur, `dossier.ombre.${nom}`);
		});
	}

	/**
	 * Reproduit une opération envoyée (ou mise en file) vers le serveur. Appelé par
	 * DynamicService APRÈS succès, avec les données telles qu'envoyées.
	 */
	appliquer(entite: string, action: ActionOmbre, donnees: any, biIdContexte: number): void {
		if (entite === 'Bi') { this.appliquerBi(action, donnees); return; }
		if (!(ENTITES as readonly string[]).includes(entite)) return;
		const e = entite as Entite;
		this.enFile('application', async () => {
			const base = this.ouvrir();
			const liste: any[] = Array.isArray(donnees) ? donnees : [donnees];
			for (const element of liste) {
				const id = action === 'delete' || action === 'bulkDelete'
					? String(element)
					: element?.id != null ? String(element.id) : null;
				if (!id) continue;

				if (action === 'delete' || action === 'bulkDelete') {
					const supprimee = await base.lignes.get([e, id]);
					await base.lignes.delete([e, id]);
					this.noterSuppression(`${e}:${id}`);
					// Cascades du serveur : une investigation supprimée emporte ses phrases (et
					// leurs questions), une phrase ses questions. L'appareil fait de même.
					if (supprimee && (e === 'RapportInvestigation' || e === 'PhraseRapport')) {
						await this.supprimerEnCascade(e, id, supprimee.biId);
					}
					continue;
				}

				const existante = await base.lignes.get([e, id]);
				const biId = biIdContexte > 0 ? biIdContexte
					: existante?.biId ?? await this.biIdDepuisRapport(element);
				if (!biId) continue;

				if (action === 'patch' || action === 'bulkPatch') {
					if (!existante) {
						await base.dossiers.where('biId').equals(biId).modify(d => { d.patchsSansLigne++; });
						continue;
					}
					existante.donnees = { ...existante.donnees, ...projeter(element) };
					existante.complete = { ...(existante.complete ?? {}), ...versComplet(e, element) };
					existante.majLe = Date.now();
					await base.lignes.put(existante);
				} else {
					// add, bulkAdd, update : l'entité complète.
					const parent = e === 'QuestionRapport'
						? String(element?.phraseRapportId ?? element?.phraseRapport?.id ?? '') || existante?.parent
						: undefined;
					await base.lignes.put({
						entite: e, id, biId, donnees: projeter(element), majLe: Date.now(),
						complete: versComplet(e, element), completeOk: true,
						// Nouvelle ligne : position « à la suite » (l'appli ajoute en fin de liste).
						// Départage les phrases de MÊME rang `sort`, affichées dans l'ordre de la
						// liste : sans position, l'ombre ne savait pas les ranger (07/10).
						parent, ordre: existante?.ordre ?? this.ordreSuivant(),
					});
				}
			}
		});
	}

	/** Positions des lignes créées sur l'appareil : après toutes celles d'un semis (index). */
	private compteurOrdre = 0;
	private ordreSuivant(): number {
		return Date.now() * 1000 + (this.compteurOrdre++ % 1000);
	}

	/** Lignes enfants supprimées par le serveur avec leur parent (voir appliquer). */
	private async supprimerEnCascade(entite: 'RapportInvestigation' | 'PhraseRapport', id: string, biId: number): Promise<void> {
		const base = this.ouvrir();
		const lignes = await base.lignes.where('biId').equals(biId).toArray();
		const phrases = entite === 'RapportInvestigation'
			? lignes.filter(l => l.entite === 'PhraseRapport'
				&& String((l.complete as any)?.investigationId ?? (l.donnees as any)?.investigationId ?? '') === id)
				.map(l => l.id)
			: [id];
		const aSupprimer = [
			...phrases.filter(p => p !== id || entite !== 'PhraseRapport').map(p => ['PhraseRapport', p]),
			...lignes.filter(l => l.entite === 'QuestionRapport' && phrases.includes(String(l.parent ?? ''))).map(l => ['QuestionRapport', l.id]),
		] as [Entite, string][];
		if (aSupprimer.length) await base.lignes.bulkDelete(aSupprimer);
	}

	/** Modification du dossier lui-même (Bi) : ses données complètes, hors rapport. */
	private appliquerBi(action: ActionOmbre, donnees: any): void {
		if (action === 'delete' || action === 'bulkDelete') return;
		this.enFile('application', async () => {
			const base = this.ouvrir();
			for (const element of Array.isArray(donnees) ? donnees : [donnees]) {
				const biId = Number(element?.id);
				if (!biId) continue;
				const dossier = await base.dossiers.get(biId);
				if (!dossier) continue;
				const complet = versComplet('Bi', element);
				dossier.bi = action === 'patch' || action === 'bulkPatch' ? { ...(dossier.bi ?? {}), ...complet } : complet;
				await base.dossiers.put(dossier);
			}
		});
	}

	/** Dossier d'une entité ajoutée hors d'un écran de rapport (ex. photo) : via son rapport. */
	private async biIdDepuisRapport(element: any): Promise<number | null> {
		const rapportId = element?.rapportId ?? element?.rapport?.id;
		if (rapportId == null) return null;
		const dossier = await this.ouvrir().dossiers.where('rapportId').equals(Number(rapportId)).first();
		return dossier?.biId ?? null;
	}

	/**
	 * Ressème le stockage unitaire depuis les dossiers reçus du serveur ET ÉCRITS dans le
	 * monolithe. DynamicService ne passe que ceux-là : un dossier protégé (modifications
	 * en attente) garde sa copie locale côté monolithe, donc aussi côté ombre.
	 *
	 * L'ombre suit ainsi exactement les écritures serveur du monolithe. Avant, elle avait
	 * sa propre règle (toute ligne de file sans biId bloquait le resemis de TOUS les
	 * dossiers) : le monolithe était rafraîchi par le serveur, l'ombre non, et la
	 * comparaison remontait des écarts qui n'étaient que cette différence de règle.
	 */
	semerDepuisServeur(bis: any[], depuis?: number): void {
		const complets = (bis || []).filter(b => b?.id && b?.rapport);
		// Instant de la requête : la copie serveur reflète le dossier à ce moment-là. Ce qui
		// a changé sur l'appareil APRÈS est plus récent qu'elle et doit être gardé ; ce qui
		// est plus ancien et absent de la copie serveur a été supprimé (côté serveur ou
		// ailleurs) et ne doit PAS être gardé.
		const instant = depuis ?? Date.now();
		if (complets.length === 0) return;
		this.enFile('semis', async () => {
			const { biIds: enAttente } = await this.dossiersEnAttente();
			let ecrases = 0;
			for (const bi of complets) {
				// Écrit dans le monolithe malgré des lignes en file (file sans biId, ou
				// au-delà de 10 essais) : on le signale, et l'ombre suit le monolithe.
				if (enAttente.has(Number(bi.id))) ecrases++;
				await this.semer(bi, 'serveur', instant);
				// Rend la main entre deux dossiers : le tableau de bord en charge des dizaines,
				// l'écran ne doit pas se figer pendant le semis.
				await new Promise(r => setTimeout(r, 0));
			}
			if (ecrases > 0) {
				this.tracer('dossier.ecrasement_en_attente', { 'dossiers.nombre': ecrases });
			}
			await this.menage(enAttente);
		});
	}

	/**
	 * Retire les dossiers inactifs depuis DUREE_CONSERVATION_MS (validés, sortis de la
	 * liste du technicien…). Jamais un dossier qui a des modifications en attente.
	 */
	private async menage(enAttente: Set<number>): Promise<void> {
		const base = this.ouvrir();
		const limite = Date.now() - DUREE_CONSERVATION_MS;
		const anciens = (await base.dossiers.toArray())
			.filter(d => (d.vuLe ?? d.semeLe) < limite && !enAttente.has(d.biId))
			.map(d => d.biId);
		for (const biId of anciens) {
			await base.transaction('rw', base.dossiers, base.lignes, async () => {
				await base.lignes.where('biId').equals(biId).delete();
				await base.dossiers.delete(biId);
			});
		}
	}

	/** Premier semis depuis la copie locale, si le dossier n'a jamais été semé. */
	/**
	 * Suppressions récentes (entité:id → instant). Une copie serveur partie AVANT une
	 * suppression contient encore la ligne : sans ce relevé, le resemis la faisait
	 * réapparaître dans l'ombre. En mémoire seulement : une suppression encore en file
	 * protège le dossier de tout resemis (DynamicService), le relevé ne sert qu'au court
	 * instant entre l'envoi réussi et l'arrivée de la copie serveur.
	 */
	private suppressions = new Map<string, number>();

	private noterSuppression(cle: string): void {
		const maintenant = Date.now();
		this.suppressions.set(cle, maintenant);
		if (this.suppressions.size > 500) {
			for (const [k, t] of this.suppressions) if (maintenant - t > 3600_000) this.suppressions.delete(k);
		}
	}

	private async semerSiAbsent(bi: any): Promise<boolean> {
		if (await this.ouvrir().dossiers.get(Number(bi.id))) return false;
		await this.semer(bi, 'local');
		return true;
	}

	private async semer(bi: any, source: 'serveur' | 'local', instant?: number, suiviComplet = false): Promise<void> {
		const base = this.ouvrir();
		const biId = Number(bi.id);
		const precedent = await base.dossiers.get(biId);
		const complet = suiviComplet || !!precedent?.complet;
		// Données complètes (phase 2) seulement pour les dossiers encore modifiés sur
		// l'appareil (non validés par le technicien) : ce sont les seuls concernés par la
		// reconstruction, et le BE en charge des dizaines déjà validés — inutile d'en payer
		// le coût (copie complète) au chargement du tableau de bord.
		const enCours = !bi.dateValidationTech || complet;
		const complets = new Map((enCours ? extraireComplet(bi) : []).map(c => [`${c.entite}:${c.id}`, c]));
		const lignes: LigneOmbre[] = extraire(bi).map(l => {
			const c = complets.get(`${l.entite}:${l.id}`);
			return { ...l, biId, complete: c?.complete, completeOk: !!c, parent: c?.parent, ordre: c?.ordre };
		});
		await base.transaction('rw', base.dossiers, base.lignes, async () => {
			// Lignes modifiées sur l'appareil APRÈS le départ de la requête : plus récentes que
			// la copie serveur, on les garde telles quelles (créations encore absentes du
			// serveur, modifications pas encore reflétées). Les jeter faisait des écarts « +M »
			// en rafale juste après un resemis.
			//
			// Avant, on gardait toute création de moins de 2 h absente du serveur : des
			// phrases SUPPRIMÉES côté serveur (changement de dommage) étaient ainsi gardées
			// dans l'ombre — 82 lignes « +O » sur BI 34198 (05/10). L'étape D les aurait fait
			// réapparaître.
			const recentes = source === 'serveur' && instant
				? (await base.lignes.where('biId').equals(biId).toArray())
					.filter(l => l.majLe && l.majLe > instant && l.completeOk)
				: [];
			const cleRecentes = new Set(recentes.map(l => `${l.entite}:${l.id}`));
			// Supprimées sur l'appareil après le départ de la requête : la copie serveur les
			// contient encore, on ne les remet pas.
			const supprimeeApres = (k: string) => source === 'serveur' && !!instant && (this.suppressions.get(k) ?? 0) > instant;
			await base.lignes.where('biId').equals(biId).delete();
			await base.lignes.bulkPut([
				...lignes.filter(l => !cleRecentes.has(`${l.entite}:${l.id}`) && !supprimeeApres(`${l.entite}:${l.id}`)),
				...recentes,
			]);
			await base.dossiers.put({
				biId, rapportId: bi.rapport?.id != null ? Number(bi.rapport.id) : null,
				source, semeLe: Date.now(), patchsSansLigne: 0, vuLe: Date.now(),
				bi: enCours ? versComplet('Bi', bi) : undefined,
				...(complet ? { complet: true } : {}),
			});
		});
	}

	/** Dossiers ayant des modifications dans la file d'envoi. */
	private async dossiersEnAttente(): Promise<{ biIds: Set<number>; inconnus: number }> {
		const biIds = new Set<number>();
		let inconnus = 0;
		await db.table('waitingtransaction').each((t: any) => {
			if (!(ENTITES as readonly string[]).includes(t?.entityName)) return;
			if (t.biId) biIds.add(Number(t.biId));
			else inconnus++;
		});
		return { biIds, inconnus };
	}

	/**
	 * Compare, à l'ouverture d'un dossier, le monolithe (tel qu'il est stocké) et le
	 * stockage unitaire. Différé et hors du chemin d'affichage.
	 */
	comparerPlusTard(biId: number): void {
		if (!this.actif() || !biId) return;
		const lancer = () => this.enFile('comparaison', () => this.comparer(biId));
		setTimeout(() => {
			const ric = (window as any).requestIdleCallback;
			if (typeof ric === 'function') ric(lancer, { timeout: 10000 });
			else lancer();
		}, 3000);
	}

	private async comparer(biId: number): Promise<void> {
		const monolithe = await db.table('bi').get(biId);
		if (!monolithe?.rapport) return;
		if (await this.semerSiAbsent(monolithe)) return; // rien à comparer au premier passage

		const base = this.ouvrir();
		await base.dossiers.update(biId, { vuLe: Date.now() });
		const dossier = await base.dossiers.get(biId);
		// Dossier validé ouvert (BE) sans données complètes : on passe en suivi complet,
		// semé depuis la copie locale. Rien à comparer à ce passage (ombre = monolithe).
		if (dossier && !dossier.bi && monolithe.dateValidationTech) {
			await this.semer(monolithe, 'local', undefined, true);
			return;
		}
		const ombre = await base.lignes.where('biId').equals(biId).toArray();
		const cle = (e: string, id: string) => `${e}:${id}`;
		const parCle = new Map(ombre.map(l => [cle(l.entite, l.id), l]));
		const vus = new Set<string>();

		let seulementMonolithe = 0;
		let champsDifferents = 0;
		// Textes identiques une fois balises et espaces retirés (mise en forme de l'éditeur) :
		// comptés à part, ils ne font pas échouer la comparaison.
		let champsBalises = 0;
		// Écarts sur des lignes modifiées localement depuis le semis : le monolithe n'a pas
		// reçu une opération que l'ombre a reçue (écriture du monolithe perdue ou en retard).
		let champsApresOp = 0;
		let opLaPlusRecenteS = -1;
		const exemples: string[] = [];
		const noter = (texte: string) => { if (exemples.length < 15) exemples.push(texte); };
		// Versions complètes du monolithe, calculées seulement s'il y a un écart de champ :
		// la projection ne garde pas les textes, d'où des écarts sans explication (19/10).
		let completsM: Map<string, Record<string, unknown>> | null = null;
		const completM = (k: string) => {
			completsM ??= new Map(extraireComplet(monolithe).map(c => [cle(c.entite, c.id), c.complete]));
			return completsM.get(k);
		};

		for (const m of extraire(monolithe)) {
			const k = cle(m.entite, m.id);
			vus.add(k);
			const o = parCle.get(k);
			if (!o) {
				seulementMonolithe++;
				noter(`+M ${m.entite}:${m.id.slice(0, 8)}`);
				continue;
			}
			for (const champ of new Set([...Object.keys(m.donnees), ...Object.keys(o.donnees)])) {
				if (!egales(m.donnees[champ], o.donnees[champ])) {
					const extrait = extraitDifference(completM(k)?.[champ], o.complete?.[champ]);
					if (extrait === BALISES_ESPACES) {
						champsBalises++;
						noter(`≈ ${m.entite}.${champ}:${m.id.slice(0, 8)}`);
						continue;
					}
					champsDifferents++;
					// @op<n>s : ligne de l'ombre modifiée par une opération il y a n s.
					let marque = '';
					if (o.majLe) {
						champsApresOp++;
						const age = Math.round((Date.now() - o.majLe) / 1000);
						if (opLaPlusRecenteS < 0 || age < opLaPlusRecenteS) opLaPlusRecenteS = age;
						marque = ` @op${age}s`;
					}
					noter(`≠ ${m.entite}.${champ}:${m.id.slice(0, 8)}${marque}${extrait}`);
				}
			}
		}
		const seulementOmbre = ombre.filter(l => !vus.has(cle(l.entite, l.id)));
		for (const l of seulementOmbre) noter(`+O ${l.entite}:${l.id.slice(0, 8)}${l.majLe ? ' @op' : ''}`);
		const vides = monolitheVide(monolithe, ombre);

		this.tracer('dossier.ombre.comparaison', {
			'bi.id': biId,
			'ombre.source': dossier?.source ?? '',
			'ombre.age_min': dossier ? Math.round((Date.now() - dossier.semeLe) / 60000) : -1,
			'ombre.lignes': ombre.length,
			'ecart.seulement_monolithe': seulementMonolithe,
			'ecart.seulement_ombre': seulementOmbre.length,
			'ecart.champs': champsDifferents,
			'ecart.champs_balises': champsBalises,
			'ecart.champs_apres_op': champsApresOp,
			'ecart.op_recente_s': opLaPlusRecenteS,
			'ecart.patchs_sans_ligne': dossier?.patchsSansLigne ?? 0,
			// Monolithe SANS aucune phrase / photo alors que l'ombre en a : copie locale
			// incomplète (dossier « minimal » de la liste BE…), pas une erreur de l'ombre.
			'ecart.monolithe_vide_phrases': vides.phrases,
			'ecart.monolithe_vide_photos': vides.photos,
			'ecart.ok': seulementMonolithe + seulementOmbre.length + champsDifferents === 0 ? 1 : 0,
			'ecart.exemples': exemples.join(' | '),
		});

		await this.mesurerReconstruction(biId, monolithe, dossier, ombre);
	}

	/**
	 * Reconstruit le dossier entier depuis le stockage unitaire (comme le fera l'étape D).
	 * Retourne null si une ligne n'est pas complète (pas encore ressemée depuis l'étape A).
	 */
	reconstruireDepuis(dossier: DossierOmbre | undefined, lignes: LigneOmbre[]): any | null {
		if (!dossier?.bi) return null;
		if (lignes.some(l => !l.completeOk || !l.complete)) return null;
		const parOrdre = (a: LigneOmbre, b: LigneOmbre) =>
			(a.ordre ?? Number.MAX_SAFE_INTEGER) - (b.ordre ?? Number.MAX_SAFE_INTEGER);
		const de = (e: Entite) => lignes.filter(l => l.entite === e).sort(parOrdre);
		// Phrases : c'est leur rang (`sort`) qui fait foi, pas leur place dans la liste au
		// premier semis — les phrases ajoutées ensuite (ajout en lot à l'ouverture du
		// rapport…) n'ont pas de place connue et finissaient en vrac à la fin (BI 34271, 02/10).
		const parRang = (a: LigneOmbre, b: LigneOmbre) =>
			rangPhrase(a.complete) - rangPhrase(b.complete) || parOrdre(a, b);
		const rapportLigne = de('Rapport')[0];
		if (!rapportLigne) return null;
		const questionsPar = new Map<string, any[]>();
		for (const q of de('QuestionRapport')) {
			const liste = questionsPar.get(q.parent ?? '') ?? [];
			liste.push(q.complete);
			questionsPar.set(q.parent ?? '', liste);
		}
		const rapport: any = {
			...rapportLigne.complete,
			phrases: lignes.filter(l => l.entite === 'PhraseRapport').sort(parRang).map(p => ({ ...p.complete, questions: questionsPar.get(p.id) ?? [] })),
			photos: de('Photo').map(p => p.complete),
			investigations: de('RapportInvestigation').map(p => p.complete),
			commentaires: de('RapportComment').map(p => p.complete),
		};
		return { ...dossier.bi, rapport };
	}

	/**
	 * ÉTAPE B — mesure seulement : reconstruit le dossier depuis le stockage unitaire et le
	 * compare, ligne par ligne et champ par champ, au monolithe. Rien n'est affiché depuis
	 * l'ombre. Objectif avant l'étape C : ≥ 99,5 % de dossiers identiques sur plusieurs jours.
	 */
	/** Dossiers déjà mesurés dans cette session : une mesure suffit (coût sur iPad). */
	private reconstructionsMesurees = new Set<number>();

	/**
	 * Délai avant de confirmer un écart. Une mesure prise PENDANT une action (changement de
	 * dommage : 46 phrases écrites dans le monolithe avant que l'ombre ne reçoive
	 * l'opération — BI 33911, 02/10) voyait un écart passager. On ne remonte un écart que
	 * s'il est encore là à la seconde lecture.
	 */
	private delaiConfirmationMs = 30_000;

	private async mesurerReconstruction(biId: number, monolithe: any, dossier: DossierOmbre | undefined, lignes: LigneOmbre[]): Promise<void> {
		if (this.reconstructionsMesurees.has(biId)) return;
		this.reconstructionsMesurees.add(biId);
		const premiere = this.analyserReconstruction(biId, monolithe, dossier, lignes);
		if (premiere['reconstruction.possible'] !== 1 || premiere['reconstruction.ok'] === 1) {
			this.tracer('dossier.ombre.reconstruction', premiere);
			return;
		}
		// Écart : seconde lecture, une fois les opérations en cours appliquées.
		setTimeout(() => this.enFile('reconstruction', async () => {
			const monolithe2 = await db.table('bi').get(biId);
			const base = this.ouvrir();
			const dossier2 = await base.dossiers.get(biId);
			const lignes2 = await base.lignes.where('biId').equals(biId).toArray();
			if (!monolithe2?.rapport || !dossier2) return; // dossier quitté ou purgé entre-temps
			const seconde = this.analyserReconstruction(biId, monolithe2, dossier2, lignes2);
			this.tracer('dossier.ombre.reconstruction', {
				...seconde,
				'reconstruction.confirmation': 1,
				// L'écart de la 1re lecture a disparu : simple décalage pendant une action.
				'reconstruction.ecart_passager': seconde['reconstruction.ok'] === 1 ? 1 : 0,
			});
		}), this.delaiConfirmationMs);
	}

	/** Compare le monolithe à sa reconstruction depuis l'ombre ; attributs de télémétrie. */
	private analyserReconstruction(biId: number, monolithe: any, dossier: DossierOmbre | undefined, lignes: LigneOmbre[]): Record<string, string | number> {
		const debut = performance.now();
		const incompletes = lignes.filter(l => !l.completeOk || !l.complete).length + (dossier?.bi ? 0 : 1);
		const reconstruit = this.reconstruireDepuis(dossier, lignes);
		const dureeMs = Math.round(performance.now() - debut);
		if (!reconstruit) {
			return {
				'bi.id': biId, 'reconstruction.possible': 0, 'reconstruction.lignes_incompletes': incompletes,
				'reconstruction.ok': 0,
			};
		}

		const exemples: string[] = [];
		const noter = (t: string) => { if (exemples.length < 15) exemples.push(t); };
		let champs = 0, champsBalises = 0, relationsAbsentes = 0, manquantes = 0, enTrop = 0;

		const comparerLignes = (nom: string, attendu: Record<string, unknown>, obtenu: Record<string, unknown>) => {
			for (const cle of new Set([...Object.keys(attendu), ...Object.keys(obtenu)])) {
				if (CHAMPS_INFORMATIFS.has(cle)) continue;
				const a = attendu[cle], o = obtenu[cle];
				// Relation (objet avec id) : présence et identité suffisent.
				if (a && typeof a === 'object' && !Array.isArray(a) && 'id' in (a as any)) {
					if (!o || typeof o !== 'object' || (o as any).id !== (a as any).id) { relationsAbsentes++; noter(`~ ${nom}.${cle}`); }
					continue;
				}
				if (jsonStable(normaliser(a)) !== jsonStable(normaliser(o))) {
					const extrait = extraitDifference(a, o);
					if (extrait === BALISES_ESPACES) { champsBalises++; noter(`≈ ${nom}.${cle}`); }
					else { champs++; noter(`≠ ${nom}.${cle}${extrait}`); }
				}
			}
		};

		// Dossier (Bi) puis chaque entité du monolithe.
		comparerLignes('Bi', versComplet('Bi', monolithe), versComplet('Bi', reconstruit));
		const index = new Map<string, Record<string, unknown>>();
		for (const c of extraireComplet(reconstruit)) index.set(`${c.entite}:${c.id}`, c.complete);
		const vus = new Set<string>();
		for (const c of extraireComplet(monolithe)) {
			const k = `${c.entite}:${c.id}`;
			vus.add(k);
			const obtenu = index.get(k);
			if (!obtenu) { manquantes++; noter(`+M ${c.entite}:${c.id.slice(0, 8)}`); continue; }
			comparerLignes(`${c.entite}:${c.id.slice(0, 8)}`, c.complete, obtenu);
		}
		for (const k of index.keys()) if (!vus.has(k)) { enTrop++; noter(`+O ${k.slice(0, 20)}`); }

		// Ordre des phrases : celui qu'affiche l'appli (rang `sort`, à égalité l'ordre de la
		// liste) contre celui de la reconstruction. Les rangs en double sont comptés à part :
		// leur ordre relatif n'est fixé par rien d'autre que la liste du monolithe.
		const phrasesM: any[] = monolithe.rapport?.phrases || [];
		const ordreM = phrasesM
			.map((p, i) => ({ id: String(p?.id), rang: rangPhrase(p), i }))
			.sort((a, b) => a.rang - b.rang || a.i - b.i)
			.map(p => p.id);
		const ordreR = (reconstruit.rapport?.phrases || []).map((p: any) => String(p?.id));
		const horsPlace = ordreM.filter((id: string, i: number) => ordreR[i] !== id).length;
		// Rangs en double DANS UNE MÊME SECTION seulement : le rang est propre à chaque
		// section (conclusion, investigation…). Comptés sur tout le dossier, 98,6 % des
		// « doubles » étaient des phrases de sections différentes, jamais affichées ensemble (19/10).
		const rangs = new Map<string, number>();
		for (const p of phrasesM) {
			const k = `${sectionPhrase(p)}#${rangPhrase(p)}`;
			rangs.set(k, (rangs.get(k) ?? 0) + 1);
		}
		const rangsEnDouble = [...rangs.values()].filter(n => n > 1).reduce((t, n) => t + n, 0);

		return {
			'bi.id': biId,
			'reconstruction.possible': 1,
			'reconstruction.duree_ms': dureeMs,
			'reconstruction.lignes': lignes.length,
			'reconstruction.manquantes': manquantes,
			'reconstruction.en_trop': enTrop,
			'reconstruction.champs': champs,
			'reconstruction.champs_balises': champsBalises,
			'reconstruction.relations_absentes': relationsAbsentes,
			'reconstruction.phrases_hors_place': horsPlace,
			'reconstruction.phrases_rang_double': rangsEnDouble,
			'reconstruction.monolithe_vide_phrases': !(phrasesM.length) && (reconstruit.rapport?.phrases?.length ?? 0) > 0 ? 1 : 0,
			'reconstruction.monolithe_vide_photos': !(monolithe.rapport?.photos?.length) && (reconstruit.rapport?.photos?.length ?? 0) > 0 ? 1 : 0,
			'reconstruction.ok': manquantes + enTrop + champs + relationsAbsentes + horsPlace === 0 ? 1 : 0,
			'reconstruction.exemples': exemples.join(' | '),
		};
	}

	private tracer(nom: string, attributs: Record<string, string | number>): void {
		try {
			const span = trace.getTracer('atlas-v2-web').startSpan(nom);
			const utilisateur = contexteUtilisateur();
			if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
			if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
			for (const [cle, valeur] of Object.entries(attributs)) span.setAttribute(cle, valeur);
			span.end();
		} catch { /* télémétrie indisponible */ }
	}
}

export const ombreDossier = new OmbreDossier();
